import { useEffect, useRef } from 'react'
import { last } from 'lodash'

// Active handlers in activation order — only the most recently activated
// overlay responds to Escape, so stacked overlays (e.g. a confirm modal over
// a wizard) peel one at a time instead of all closing on a single keypress.
const escapeHandlers = []

export function useEscapeKey(callback, enabled = true) {
  const callbackRef = useRef(callback)
  const active = enabled && Boolean(callback)

  // Keep the ref pointed at the latest callback without re-binding the
  // listener on every render — callers typically pass a fresh closure.
  useEffect(() => {
    callbackRef.current = callback
  })

  useEffect(() => {
    if (!active) {
      return undefined
    }

    const handleKeyDown = (event) => {
      // Skip if a nested control already handled Escape (e.g. a react-select
      // dropdown open inside a Drawer calls preventDefault) so we don't close
      // the outer overlay along with the inner one.
      if (
        event.key === 'Escape' &&
        !event.defaultPrevented &&
        last(escapeHandlers) === handleKeyDown
      ) {
        callbackRef.current?.()
      }
    }

    escapeHandlers.push(handleKeyDown)
    document.addEventListener('keydown', handleKeyDown)

    return () => {
      escapeHandlers.splice(escapeHandlers.indexOf(handleKeyDown), 1)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [active])
}
